<script setup lang="ts">
import { useRoute } from 'vue-router'
import type { DataTableHeaders } from '@/plugins/vuetify'
import type { HDHTTPDevice, HDDownlinkMessage, HDUplinkMessage } from '@/types/httpdevices'



const router = useRouter()
const route = useRoute()
const customerId = (route.params as { customerId: string }).customerId
const deviceId = (route.params as { id: string }).id
const isEdit = ref(deviceId !== '0')

const device = ref({
  id: deviceId === '0' ? '' : deviceId,
  name: '',
  accessToken: '',
  thingsBoardCustomerId: customerId,
  thingsBoardId: '',
  thingsBoardAccessToken: '',
  lastDownlinkConfirmedId: 0,
} as HDHTTPDevice)

const downlinks = ref ([] as HDDownlinkMessage[])
const headersDownlink: DataTableHeaders = [
  { title: 'Date (UTC)', key: 'date', width: '250px' },
  { title: 'Status', key: 'status', width: '150px' },
  { title: 'Confirmed Id', key: 'confirmedId', width: '150px' },
  { title: 'Data', key: 'data' },
  { title: 'Actions', key: 'actions', width: '100px', sortable: false }
]

const dialogDelete = useTemplateRef('dialogDelete')
async function deleteDownlink(downlink: HDDownlinkMessage) {
  const result = await dialogDelete.value?.open('Êtes-vous certain de vouloir supprimer ce downlink ?');
  if (!result?.valueOf())
    return

  try {
    await apiOmni.delete(`customers/${customerId}/httpdevices/${deviceId}/downlink/${downlink.id}`)
    Notify.success('Supprimé')
    await loadDownlinks()
  } catch (error) {
    Notify.error(error)
  }
}

async function loadDownlinks() {
  const result = await apiOmni.get(`customers/${customerId}/httpdevices/${deviceId}/downlink`)
  if (result.data) {
    downlinks.value = result.data
  }
}

const dialogNewDownlink = ref(false)
const downlinkData = ref('')
const downlinkConfirmed = ref(true)
function newDownlink() {
  dialogNewDownlink.value = true
}

async function confirmDownlink() {
  try {
    const config = { params: { confirmed: downlinkConfirmed.value } }
    await apiOmni.post(`customers/${customerId}/httpdevices/${deviceId}/downlink`, downlinkData.value, config)
    Notify.success('Envoyé')
    await loadDownlinks()
  } catch (error) {
    Notify.error(error)
  }

  dialogNewDownlink.value = false
}

function cancelDownlink() {
  dialogNewDownlink.value = false
}

const uplinks = ref ([] as HDUplinkMessage[])
const headersUplink: DataTableHeaders = [
  { title: 'Date (UTC)', key: 'date', width: '250px' },
  { title: 'Data', key: 'data' }
]

onMounted(async () => {
  try {
    if (isEdit.value) {
      const result = await apiOmni.get(`customers/${customerId}/httpdevices/${deviceId}`)
      if (result.data) {
        device.value = result.data

        const result2 = await apiOmni.get(`customers/${customerId}/httpdevices/${deviceId}/uplink`)
        if (result2.data) {
          uplinks.value = result2.data
        }

        await loadDownlinks()
      }
    }
  } catch (error) {
    Notify.error(error)
  }
})

const submitForm = async () => {
  try {
    if (isEdit.value) {
      await apiOmni.put(`customers/${customerId}/httpdevices/${deviceId}`, device.value)
      Notify.success("Modification de l'appareil http avec succès");
    } else {
      await apiOmni.post(`customers/${customerId}/httpdevices`, device.value)
      Notify.success("Création de l'appareil http avec succès");
    }
  } catch (error) {
    Notify.error(error)
    return
  }

  router.push(`/httpdevices`)
}
</script>

<template>
  <v-container fluid>
    <v-form @submit.prevent="submitForm">
      <v-row>
        <v-col cols="6" md="6">
          <v-text-field v-model="device.id" label="ID" variant="solo" readonly/>
          <v-text-field v-model="device.name" label="Name" required/>
          <v-text-field v-model="device.accessToken" label="Access Token" required/>
          <v-text-field v-model="device.lastDownlinkConfirmedId" label="Last Downlink Confirmed ID" variant="solo" readonly/>
          <v-btn type="submit" color="primary">{{ isEdit ? "Modifier" : "Créer" }}</v-btn>
        </v-col>
        <v-col cols="6" md="6">
          <v-text-field v-model="device.thingsBoardCustomerId" label="ThingsBoard Customer ID" variant="solo" readonly/>
          <v-text-field v-model="device.thingsBoardId" label="ThingsBoard ID" required/>
          <v-text-field v-model="device.thingsBoardAccessToken" label="ThingsBoard Access Token" required/>
        </v-col>
      </v-row>
      <v-row v-if="isEdit">
        <v-col cols="12" md="12">
          <v-data-table :headers="headersDownlink" :items="downlinks" item-key="id">
            <template #top>
              <v-toolbar flat>
                <v-toolbar-title>Downlinks</v-toolbar-title>
                <v-divider class="mx-4" inset vertical/>
                <v-spacer/>
                <v-btn class="mb-2" color="primary" @click="newDownlink">Nouveau downlink</v-btn>
              </v-toolbar>
            </template>
            <template #item.actions="{ item }">
              <v-icon size="small" @click="deleteDownlink(item)">
                M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z
              </v-icon>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row v-if="isEdit">
        <v-col cols="12" md="12">
          <v-data-table :headers="headersUplink" :items="uplinks" item-key="id">
            <template #top>
              <v-toolbar flat>
                <v-toolbar-title>Uplinks</v-toolbar-title>
              </v-toolbar>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
  <DialogConfirm ref="dialogDelete" />
  <v-dialog v-model="dialogNewDownlink" max-width="400px">
    <v-card style="z-index: -1">
      <v-card-title>
        <span class="text-h5">Downlink</span>
      </v-card-title>
      <v-card-text>
        <v-text-field v-model="downlinkData" label="Data" required/>
        <v-checkbox v-model="downlinkConfirmed" label="Confirm" required/>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" @click="cancelDownlink">Cancel</v-btn>
        <v-btn color="primary" @click="confirmDownlink">Confirm</v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
